import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { htmlSafe } from '@ember/template';
import { gt } from 'ember-truth-helpers';
import add from 'madow-tech/helpers/add';

interface ImageLightboxSignature {
  Args: {
    isOpen: boolean;
    imageUrl: string;
    imageIndex: number;
    gallery: string[];
    onClose: () => void;
    onNext?: () => void;
    onPrev?: () => void;
  };
}

export default class ImageLightbox extends Component<ImageLightboxSignature> {
  @tracked zoomLevel = 1;

  @action
  handleClose(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.zoomLevel = 1;
    this.args.onClose();
  }

  @action
  handleBackdropClick(event: MouseEvent) {
    // Solo cerrar si se hace click en el backdrop, no en la imagen
    if (event.target === event.currentTarget) {
      this.handleClose();
    }
  }

  @action
  preventClose(event: Event) {
    event.stopPropagation();
  }

  @action
  handleNext() {
    this.zoomLevel = 1;
    if (this.args.onNext) {
      this.args.onNext();
    }
  }

  @action
  handlePrev() {
    this.zoomLevel = 1;
    if (this.args.onPrev) {
      this.args.onPrev();
    }
  }

  @action
  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
  }

  @action
  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
  }

  @action
  resetZoom() {
    this.zoomLevel = 1;
  }

  get zoomStyle() {
    return htmlSafe(`transform: scale(${this.zoomLevel})`);
  }

  @action
  handleKeydown(event: KeyboardEvent) {
    if (!this.args.isOpen) return;

    switch (event.key) {
      case 'Escape':
        this.handleClose();
        break;
      case 'ArrowLeft':
        this.handlePrev();
        break;
      case 'ArrowRight':
        this.handleNext();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case '0':
        this.resetZoom();
        break;
    }
  }

  <template>
    {{#if @isOpen}}
      {{! template-lint-disable no-invalid-interactive }}
      <div
        class="fixed inset-0 !z-[99999] bg-black/95 flex items-center justify-center p-4"
        {{on "click" this.handleBackdropClick}}
        {{on "keydown" this.handleKeydown}}
        role="dialog"
        aria-modal="true"
        aria-label="Image lightbox"
        tabindex="-1"
      >
        {{! Close button }}
        <button
          type="button"
          class="lightbox-button lightbox-close-button"
          {{on "click" this.handleClose}}
          aria-label="Close lightbox"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {{! Zoom controls }}
        <div class="lightbox-zoom-controls" {{on "click" this.preventClose}}>
          <button
            type="button"
            class="lightbox-button lightbox-zoom-button"
            {{on "click" this.zoomOut}}
            aria-label="Zoom out"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <button
            type="button"
            class="lightbox-button lightbox-zoom-display"
            {{on "click" this.resetZoom}}
            aria-label="Reset zoom"
          >
            {{this.zoomLevel}}x
          </button>
          <button
            type="button"
            class="lightbox-button lightbox-zoom-button"
            {{on "click" this.zoomIn}}
            aria-label="Zoom in"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
          </button>
        </div>

        {{! Image counter }}
        <div class="lightbox-counter" {{on "click" this.preventClose}}>
          {{add @imageIndex 1}} / {{@gallery.length}}
        </div>

        {{! Navigation buttons }}
        {{#if (gt @gallery.length 1)}}
          <button
            type="button"
            class="lightbox-button lightbox-nav-button lightbox-nav-button-prev"
            {{on "click" this.handlePrev}}
            aria-label="Previous image"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            class="lightbox-button lightbox-nav-button lightbox-nav-button-next"
            {{on "click" this.handleNext}}
            aria-label="Next image"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        {{/if}}

        {{! Image container }}
        <div class="relative flex items-center justify-center" {{on "click" this.preventClose}}>
          <img
            src={{@imageUrl}}
            alt="Fullscreen view"
            class="lightbox-image"
            style={{this.zoomStyle}}
          />
        </div>

        {{! Keyboard shortcuts hint }}
        <div class="lightbox-shortcuts-hint" {{on "click" this.preventClose}}>
          ← → Navigate • + − Zoom • ESC Close
        </div>
      </div>
    {{/if}}
  </template>
}
